<!-- Organisation Bulk User Form - With Size Limits -->
<template>
    <page-loading-animation v-if="isLoading" :is-loading="isLoading" />

    <div v-else>
        <!--Information-->
        <app-text>
            Here, you can bulk create Users for your Organisation by uploading a ZIP file containing user profile images.
            <br /><br />
            User accounts will be automatically created based on the image filenames.
            <br /><br />
            Please note, these accounts will be disabled and cannot be used to log in. Manager accounts that require login access must be created
            individually.
        </app-text>

        <v-divider class="greyD my-4" />

        <!--ZIP Instructions-->
        <app-text>
            Create a ZIP file containing profile images:
            <ul>
                <li>
                    <code>Image filenames</code> - Use either format:
                    <ul>
                        <li>camelCase: firstnameLastname.jpg (e.g., johnSmith.jpg)</li>
                        <li>PascalCase: FirstnameLastname.jpg (e.g., JohnSmith.jpg)</li>
                    </ul>
                </li>
                <li><code>Supported formats</code> - .jpg, .jpeg, .png</li>
                <li><code>Recommended file size</code> - 1MB</li>
                <li><code>Maximum ZIP file size</code> - 10MB</li>
                <li><code>Recommended batch size</code> - 10-15 images per ZIP file</li>
                <li>
                    <code>Examples</code>:
                    <ul>
                        <li>johnSmith.jpg → creates user "John Smith"</li>
                        <li>JohnSmith.jpg → creates user "John Smith"</li>
                    </ul>
                </li>
            </ul>
        </app-text>

        <!--Photograph Requirements-->
        <app-text class="mt-4">
            <div class="title red--text">PHOTOGRAPHS</div>
            <ul>
                <li>
                    Photographs need to be passport style, but can be taken from any smart phone, camera, or web-cam. Please ensure photos are as
                    clear as possible and only of one person.
                </li>
                <li>
                    Poor quality, 'holiday snaps', or photographs with more than one person, can result in a delay in your accreditation being
                    accepted, and could result in access being refused.
                </li>
            </ul>
        </app-text>

        <!--Photograph Guidelines-->
        <app-text class="mt-2">
            Your photo should conform to the following guidelines:
            <ul>
                <li>Head and top of shoulders captured in the photo</li>
                <li>Facing forward and looking straight at the camera</li>
                <li>With a neutral expression and your mouth closed</li>
                <li>Without anything covering the face</li>
                <li>In clear contrast to the background</li>
                <li>Without a head covering (unless it's worn for religious or medical reasons)</li>
                <li>With eyes open, visible and free from reflection or glare from glasses</li>
                <li>With your eyes not covered by sunglasses, tinted glasses, glasses frames or hair</li>
                <li>Without any red eye</li>
                <li>Against a white, light grey or cream background without any shadows in the picture</li>
                <li>No commercial marks visible</li>
                <li>Photos should be in JPG format for best quality</li>
            </ul>
        </app-text>

        <!--Size error alert-->
        <v-alert v-if="sizeError" type="error" class="mt-4 bg-red" style="color: white !important; background-color: #cc0e23 !important">
            <app-text style="color: white !important">
                <strong>File too large:</strong> The selected ZIP file ({{ formatFileSize(zipFile.size) }}) exceeds the maximum allowed size of 10MB.
            </app-text>
            <app-text class="mt-2" style="color: white !important">
                Please reduce the file size by:
                <ul style="color: white !important">
                    <li>Using fewer images per ZIP file (10-15 recommended)</li>
                    <li>Optimising images to be under 1MB each</li>
                    <li>Using JPEG format at medium quality</li>
                </ul>
            </app-text>
        </v-alert>

        <!--Files-->
        <div class="d-flex mt-4">
            <!--Hidden-->
            <input @change="handleZIP" accept=".zip" ref="zipInput" style="display: none" type="file" />

            <!--Visible-->
            <app-btn
                @click.native="onZipClick"
                :color="zipFile && !sizeError ? 'green' : 'primary'"
                :icon="zipFile ? '' : 'add'"
                :label="zipFile ? `ZIP File Added (${formatFileSize(zipFile.size)})` : 'Add a ZIP File'"
            />
        </div>

        <!--Upload-->
        <div class="d-flex justify-end mt-4">
            <app-btn @click.native="uploadFiles" color="green" :disabled="!zipFile || sizeError || isUploading" :label="uploadButtonLabel" />
        </div>

        <!--Results Dialog-->
        <v-dialog max-width="512" v-model="isResultsDialogVisible">
            <div class="appGrey rounded-lg pa-4">
                <!--Successful-->
                <div v-if="uploadResults?.successfulRecords?.length">
                    <app-text size="normal-bold"> {{ uploadResults.successfulRecords.length }} accounts were successfully created: </app-text>

                    <div v-for="record in uploadResults.successfulRecords" :key="record.userEmail" class="d-flex align-center mt-4">
                        <app-icon class="mr-4" color="green" icon="check" size="24" />
                        <div>
                            <app-text>{{ record.userName }}</app-text>
                            <app-text color="grey9" size="small">{{ record.userEmail }}</app-text>
                        </div>
                    </div>
                </div>

                <!--Failed-->
                <div v-if="uploadResults?.failedRecords?.length">
                    <app-text size="normal-bold"> {{ uploadResults.failedRecords.length }} accounts were not created: </app-text>

                    <div v-for="record in uploadResults.failedRecords" :key="record.fileName || record.userEmail" class="d-flex align-center mt-4">
                        <app-icon class="mr-4" color="red" icon="close" size="24" />
                        <div>
                            <app-text>{{ record.userName || record.fileName }}</app-text>
                            <app-text color="grey9" size="small" v-if="record.userEmail">{{ record.userEmail }}</app-text>
                            <app-text color="red" size="small">{{ record.error }}</app-text>
                        </div>
                    </div>
                </div>

                <v-divider class="mt-4" />

                <!--Close Button-->
                <div class="mt-4">
                    <app-btn @click.native="closeResultsDialog" :block="true" label="Close" />
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'OrganisationBulkUserForm',

    props: ['formData'],

    data: () => ({
        isLoading: false,
        isUploading: false,
        isResultsDialogVisible: false,
        uploadResults: {},
        zipFile: null,
        sizeError: false,
        // Maximum ZIP file size (10MB)
        MAX_ZIP_SIZE: 10 * 1024 * 1024
    }),

    computed: {
        uploadButtonLabel() {
            return this.isUploading ? 'Uploading...' : 'Upload Files'
        }
    },

    methods: {
        closeResultsDialog() {
            const t = this
            t.isResultsDialogVisible = false
            t.$emit('emitReloadPage')
        },

        handleZIP(event) {
            this.zipFile = event.target.files[0]

            // Check if file exceeds size limit
            this.sizeError = this.zipFile && this.zipFile.size > this.MAX_ZIP_SIZE

            if (this.sizeError) {
                console.warn(`File too large: ${this.formatFileSize(this.zipFile.size)}`)
            }
        },

        onZipClick() {
            this.$refs.zipInput.click()
        },

        openResultsDialog() {
            this.isResultsDialogVisible = true
        },

        /**
         * Format file size in human-readable format
         */
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'

            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))

            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        },

        async uploadFiles() {
            const t = this
            if (!t.zipFile) return

            // Double-check file size before upload
            if (t.zipFile.size > t.MAX_ZIP_SIZE) {
                t.sizeError = true
                t.$sharedState.errorMessage = `File too large (${t.formatFileSize(t.zipFile.size)}). Maximum allowed size is ${t.formatFileSize(
                    t.MAX_ZIP_SIZE
                )}.`
                return
            }

            t.isLoading = true
            t.isUploading = true

            try {
                // Get current user data
                const currentUser = t.MIX_getCurrentUser()

                // Construct the user config data
                const USER_CONFIG_DATA = {
                    userOrganisation: t.$props.formData.organisationData.entityId,
                    organisationDomain: t.$props.formData.organisationData.organisationName.toLowerCase(),
                    userType: 'Organisation',
                    userRole: 'User',
                    userLevel: 'Organisation-User',
                    createdUserId: currentUser.entityId,
                    createdUserName: currentUser.userName
                }

                // Construct a FormData object
                const formData = new FormData()
                formData.append('zip', t.zipFile)
                formData.append('userConfigData', JSON.stringify(USER_CONFIG_DATA))

                const RESPONSE = await t.MIX_redis_bulkCreateUsers(formData)

                if (RESPONSE.hasErrors) {
                    console.error('Error bulk creating users: ', RESPONSE.errors)
                    t.$sharedState.errorMessage = 'There was a problem creating these users, please try again.'
                    return
                }

                t.uploadResults = RESPONSE.data
                t.openResultsDialog()
            } catch (error) {
                console.error('Error in upload:', error)

                // Check if it's a payload too large error
                if (error?.message?.includes('413') || error?.toString()?.includes('Payload Too Large')) {
                    t.$sharedState.errorMessage = 'The file is too large. Please reduce the size of your ZIP file or upload fewer images.'
                } else {
                    t.$sharedState.errorMessage = 'An unexpected error occurred during upload. Please try again with a smaller file or fewer images.'
                }
            } finally {
                t.isUploading = false
                t.isLoading = false
            }
        }
    }
}
</script>
